import styles from './project-memory-book-actions.module.less';
import { useState } from 'react';
import {
    LIGHT_A_CANDLE_TEXT_KEY,
    SOMETHING_WENT_WRONG_ERROR_TEXT_KEY,
    CANDLE_LIT_SUCCESS_TEXT_KEY,
    CANDLE_WILL_BE_SHOWN_TEXT_KEY,
    NAME_WILL_BE_SHOWN_TEXT_KEY,
    LIGHT_CANDLE_SUBMIT_TEXT_KEY,
    YOUR_NAME_TEXT_KEY,
    EMAIL_TEXT_KEY,
    CANDLE_IS_LIT_TEXT_KEY,
    OPTIONAL_TEXT_KEY,
} from '../i18n/project-i18n-model';
import { useI18nTexts } from '../i18n/useI18nTexts';
import { isValidEmail, emailErrorMessage, isValidName, getNameErrorMessage } from '../../user/user-model';
import { Input, InputGroup } from '@sb1/ffe-form-react';
import { ContextMessage } from '@sb1/ffe-messages-react';
import { PrimaryButton } from '@sb1/ffe-buttons-react';
import { useMemoryBookAction } from './useMemoryBookAction';
import MemoryBookActionPage from './MemoryBookActionPage';
import {
    CANDLE_REACTION,
    fetchProjectCard,
    fetchProjectTransactionAggregates,
    postCommentWithoutTransaction,
} from '../project-actions';
import { Heading2 } from '@sb1/ffe-core-react';
import { trackEvent } from '../../support-and-tracking/analytics-actions';
import { getProjectFromId } from '../projects-reducer';

const validateForm = ({ name, email }) => ({
    nameError: name && !isValidName(name) ? getNameErrorMessage() : '',
    emailError: isValidEmail(email) ? '' : emailErrorMessage(),
});

const ProjectLightCandle = () => {
    const {
        project,
        dispatch,
        isSubmitted,
        setIsSubmitted,
        isLoading,
        setIsLoading,
        error,
        setError,
        dismissSupport,
        navigateToPayment,
        defaultValues,
    } = useMemoryBookAction('light-candle');
    const [name, setName] = useState(defaultValues.name);
    const [email, setEmail] = useState(defaultValues.email);
    const [nameError, setNameError] = useState('');
    const [emailError, setEmailError] = useState('');
    const [submittedCommentId, setSubmittedCommentId] = useState(null);
    const texts = useI18nTexts([
        SOMETHING_WENT_WRONG_ERROR_TEXT_KEY,
        CANDLE_LIT_SUCCESS_TEXT_KEY,
        CANDLE_WILL_BE_SHOWN_TEXT_KEY,
        NAME_WILL_BE_SHOWN_TEXT_KEY,
        LIGHT_CANDLE_SUBMIT_TEXT_KEY,
        YOUR_NAME_TEXT_KEY,
        EMAIL_TEXT_KEY,
        LIGHT_A_CANDLE_TEXT_KEY,
        CANDLE_IS_LIT_TEXT_KEY,
        OPTIONAL_TEXT_KEY,
    ]);

    const onSubmit = (e) => {
        e.preventDefault();
        const { nameError: nameErr, emailError: emailErr } = validateForm({ name, email });
        setNameError(nameErr);
        setEmailError(emailErr);
        if (nameErr || emailErr) {
            return;
        }
        setIsLoading(true);
        dispatch(
            postCommentWithoutTransaction({
                projectId: project.id,
                authorName: name,
                email,
                reaction: CANDLE_REACTION,
            })
        )
            .then((comment) => {
                setIsSubmitted(true);
                setSubmittedCommentId(comment?.body?.id);
                window.scrollTo(0, 0);
                trackEvent('project-light-candle', 'success', { projectId: project.id });
            })
            .catch(() => setError(texts[SOMETHING_WENT_WRONG_ERROR_TEXT_KEY]))
            .finally(() => setIsLoading(false));
    };

    return (
        <MemoryBookActionPage
            project={project}
            headingText={isSubmitted ? `${texts[CANDLE_IS_LIT_TEXT_KEY]} 🕯` : `${texts[LIGHT_A_CANDLE_TEXT_KEY]} 🕯`}
            isLoading={isLoading}
            isSubmitted={isSubmitted}
            successMessage={texts[CANDLE_LIT_SUCCESS_TEXT_KEY]}
            onDismiss={dismissSupport}
            onNavigateToPayment={() => navigateToPayment({ memorialCommentId: submittedCommentId, authorName: name })}
        >
            <Heading2 lookLike={6}>{texts[LIGHT_A_CANDLE_TEXT_KEY]}</Heading2>
            <p>{texts[CANDLE_WILL_BE_SHOWN_TEXT_KEY]}</p>
            <form onSubmit={onSubmit} className={styles.formContainer}>
                <div>
                    <InputGroup
                        label={`${texts[YOUR_NAME_TEXT_KEY]} ${texts[OPTIONAL_TEXT_KEY]}`}
                        description={texts[NAME_WILL_BE_SHOWN_TEXT_KEY]}
                        fieldMessage={nameError || undefined}
                        extraMargin={false}
                    >
                        <Input
                            value={name}
                            maxLength={50}
                            onChange={(e) => setName(e.target.value)}
                            onBlur={() => setNameError(name && !isValidName(name) ? getNameErrorMessage() : '')}
                        />
                    </InputGroup>
                </div>
                <div>
                    <InputGroup
                        label={texts[EMAIL_TEXT_KEY]}
                        fieldMessage={emailError || undefined}
                        extraMargin={false}
                    >
                        <Input
                            value={email}
                            maxLength={50}
                            onChange={(e) => setEmail(e.target.value)}
                            onBlur={() => setEmailError(isValidEmail(email) ? '' : emailErrorMessage())}
                        />
                    </InputGroup>
                </div>
                {error && <ContextMessage type="error">{error}</ContextMessage>}
                <div>
                    <PrimaryButton type="submit" isLoading={isLoading}>
                        {texts[LIGHT_CANDLE_SUBMIT_TEXT_KEY]}
                    </PrimaryButton>
                </div>
            </form>
        </MemoryBookActionPage>
    );
};

export const loader = async ({ getState, dispatch, params }) => {
    const { projectId } = params;
    await dispatch(fetchProjectCard(projectId));
    await dispatch(fetchProjectTransactionAggregates(projectId));

    const state = getState();
    const project = getProjectFromId(state, projectId);
    if (!project?.created_at) {
        throw new Response(null, { status: 404 });
    }
};

export default ProjectLightCandle;
